import {Button} from "@zendeskgarden/react-buttons";
import {Inline} from "@zendeskgarden/react-loaders";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import "native-injects";
import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";
import {FixedSizeList} from "react-window";
import styled from "styled-components";
import {userIsAdmin} from "../../authorization/AuthorizationCheckers";
import AnalystPayrollDrawer from "../../components/analyst-payroll-drawer/AnalystPayrollDrawer";
import {DateFormatter} from "../../components/analyst-payroll-drawer/helpers/utils";
import {AnalystSections} from "../../components/analyst/section-tabs/AnalystSectionTabs";
import {COLORS} from "../../constants/Colors";
import {ViziblyTheme} from "../analyst/ViziblyZDGTheme";
import {TLockedProperties} from "../bulk-update/BulkUpdate";
import {usePayrollEmployeeData} from "./PayrollDataLoader";
import * as css from "./styles/payrollStyles.module.scss";

import {History, Lock, SearchRounded} from "@material-ui/icons";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {Body as ModalBody, Close, Header, Modal} from "@zendeskgarden/react-modals";
import {Tooltip} from "@zendeskgarden/react-tooltips";
import {ReactComponent as PencilIcon} from '@zendeskgarden/svg-icons/src/16/pencil-stroke.svg';
import {ReactComponent as PlusIcon} from '@zendeskgarden/svg-icons/src/16/plus-stroke.svg';
import {ReactComponent as XIcon} from '@zendeskgarden/svg-icons/src/16/x-stroke.svg';
import {toast} from "react-toastify";
import {
    JobStatus,
    JobType,
    PayrollBonusFrequency,
    PayrollBonusType,
    PayrollCompensationItemCalculationType,
    PayrollCompensationItemType,
    PayrollPayFrequency,
    PayrollRaiseFrequency,
    PayrollRaiseType,
    useCreatePayrollEmployeeManyBonusesMutation,
    useCreatePayrollEmployeeManyOvertimesMutation,
    useCreatePayrollEmployeeManyRaisesMutation,
    useDeleteCompItemsFromEmployeesMutation,
    useDeleteEmployeesMutation,
    useDeletePayrollEmployeeManyBonusesMutation,
    useDeletePayrollEmployeeManyOvertimesMutation,
    useDeletePayrollEmployeeManyRaisesMutation,
    useGetJobLazyQuery,
    useSetPayrollEmployeeManyCompItemsMutation,
    useUpdatePayrollEmployeeManyBonusesMutation,
    useUpdatePayrollEmployeeManyOvertimesMutation,
    useUpdatePayrollEmployeeManyRaisesMutation
} from "../../__generated__/generated_types";
import {CustomZDDropdown} from "../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {AuthContext} from "../../contexts/AuthContext";
import {Property as PropertyContext, useProperties} from "../../contexts/properties/PropertiesContext";
import {dateUtcNow, ensureDateInLocale} from "../../utils/helper-funcs";
import {ListJobs, ListJobsDisplayColumn} from "../admin/jobs/ListJobs";
import {RunningJobRenderer} from "../admin/jobs/RunningJobRenderer";
import {
    acquisitionPropertyDateDisplayString,
    transposeAcquisitionDateDisplayToDatabase
} from "../workflows/account/formula-bar/logic/utils";
import {BulkDeleteConfirm} from "./components/BulkDeleteConfirm";
import {BulkUpdateBenefitTaxIns} from "./components/BulkUpdateBenefitTaxIns";
import {BulkUpdateBonusRaise, Type as BulkUpdateBonusRaiseType} from "./components/BulkUpdateBonusRaise";
import {BulkUpdateOvertime} from "./components/BulkUpdateOvertime";
import PayrollDateFilter from "./components/PayrollDateFilter";
import {PayrollTaxPercentFilter} from "./components/PayrollTaxPercentFilter";
import {
    buildPayrollDataRows,
    buildTaxFilterOptions,
    countUnlockedPayrollEmployees,
    DEFAULT_TAX_FILTER_OPTIONS,
    filterPayrollEmployees,
    getPositions
} from "./logic";
import {DEFAULT_TAX_FILTER, PayrollEmployeeDataRow, TaxFilter, TaxFilterOptions} from "./types";
import {PayrollEmployeeUploadButton} from "./components/csvbox/PayrollEmployeeUploadButton";
import {useFetchPayrollEmployeesUploadTemplate} from "./logic/useFetchPayrollEmployeesUploadTemplate";


const ScrollableTable = styled(Table).attrs({ role: 'presentation' })`
  /* stylelint-disable-next-line */
  display: block !important;
`;

const ScrollableHead = styled(Head)`
  display: block;
  z-index: 2;
`;

const ScrollableHeaderRow = styled(HeaderRow).attrs({ role: 'row' })`
  /* stylelint-disable-next-line */
  display: table !important;
  width: 100%;
  table-layout: fixed;
`;

const ScrollableHeaderCell = styled(HeaderCell).attrs({ role: 'columnheader' })`
  /* stylelint-disable-next-line no-empty-source */
`;

const ScrollableBody = styled(Body)`
  /* stylelint-disable-next-line declaration-no-important */
  display: block !important;
  z-index: 1;
`;

const ScrollableRow = styled(Row).attrs({ role: 'row' })`
  /* stylelint-disable-next-line declaration-no-important */
  display: table !important;
  table-layout: fixed;
`;

const ScrollableCell = styled(Cell).attrs({ role: 'cell' })`
  /* stylelint-disable-next-line no-empty-source */
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SuppressExtraDiv({children}: any): ReactElement {
    return (
        <>
            {children}
        </>
    );
}

function payrollCompItemConfigPayPeriodText(frequency?: PayrollPayFrequency | null): string {
    if(frequency === PayrollPayFrequency.Monthly) {
        return "Cost per Month";
    } else if(frequency === PayrollPayFrequency.SemiMonthly) {
        return "Cost Paid Twice Monthly";
    }
    return "Cost per Pay Period";
}

export function Payroll(props: {
    setSectionReady: (sectionId: AnalystSections, isReady: boolean) => void
    lockedProperties: TLockedProperties,
    filterPropertyIds: string[] | undefined,
    budgetYear: number,
    showSingleProperty?: string,
}): ReactElement {
    const { currentProperty } = useProperties();
    const {user} = useContext(AuthContext);
    const {
        payrollEmployeeData,
        payrollEmployeeDataLoading,
        payrollEmployeeDataRefetch,
        payrollCompItemConfig
    } = usePayrollEmployeeData({budgetYear: props.budgetYear, currentProperty});
    const {fetchTemplate} = useFetchPayrollEmployeesUploadTemplate();

    const [deleteEmployees, {loading: deleteEmployeesLoading}] = useDeleteEmployeesMutation();

    const [bulkUpdateCompItems, {loading: bulkUpdateCompItemsLoading}] = useSetPayrollEmployeeManyCompItemsMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkCreateBonuses, {loading: bulkCreateBonusesLoading}] = useCreatePayrollEmployeeManyBonusesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkCreateRaises, {loading: bulkCreateRaisesLoading}] = useCreatePayrollEmployeeManyRaisesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkCreateOvertimes, {loading: bulkCreateOvertimesLoading}] = useCreatePayrollEmployeeManyOvertimesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkUpdateBonuses, {loading: bulkUpdateBonusesLoading}] = useUpdatePayrollEmployeeManyBonusesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkUpdateRaises, {loading: bulkUpdateRaisesLoading}] = useUpdatePayrollEmployeeManyRaisesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkUpdateOvertimes, {loading: bulkUpdateOvertimesLoading}] = useUpdatePayrollEmployeeManyOvertimesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkDeleteBonuses, {loading: bulkDeleteBonusesLoading}] = useDeletePayrollEmployeeManyBonusesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkDeleteRaises, {loading: bulkDeleteRaisesLoading}] = useDeletePayrollEmployeeManyRaisesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkDeleteOvertimes, {loading: bulkDeleteOvertimesLoading}] = useDeletePayrollEmployeeManyOvertimesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [bulkDeleteCompItems, {loading: bulkDeleteCompItemsLoading}] = useDeleteCompItemsFromEmployeesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [showAnalystPayrollDrawer, setShowAnalystPayrollDrawer] = useState<boolean>(false);
    const [editPayrollEmplloyeeId, setEditPayrollEmployeeId] = useState<string|undefined>(undefined);
    const [payrollEmployeeDataRows, setPayrollEmployeeDataRows] = useState<PayrollEmployeeDataRow[]>();
    const [selectedPayrollEmployees, setSelectedPayrollEmployees] = useState<Set<string>>(new Set());
    const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
    const [bulkUpdateCompItemType, setBulkUpdateCompItemType] = useState<PayrollCompensationItemType>();
    const [showBulkCreateRaises, setShowBulkCreateRaises] = useState(false);
    const [showBulkCreateBonuses, setShowBulkCreateBonuses] = useState(false);
    const [showBulkCreateOvertimes, setShowBulkCreateOvertimes] = useState(false);
    const [showBulkUpdateRaises, setShowBulkUpdateRaises] = useState(false);
    const [showBulkUpdateBonuses, setShowBulkUpdateBonuses] = useState(false);
    const [showBulkUpdateOvertimes, setShowBulkUpdateOvertimes] = useState(false);
    const [showBulkDeleteEmployees, setShowBulkDeleteEmployees] = useState(false);
    const [showBulkDeleteRaises, setShowBulkDeleteRaises] = useState(false);
    const [showBulkDeleteBonuses, setShowBulkDeleteBonuses] = useState(false);
    const [showBulkDeleteOvertimes, setShowBulkDeleteOvertimes] = useState(false);
    const [showBulkDeleteCompItems, setShowBulkDeleteCompItems] = useState(false);
    const [bulkDeleteCompItemType, setBulkDeleteCompItemType] = useState<PayrollCompensationItemType | null>(null);
    const [employeeNameSearch, setEmployeeNameSearch] = useState<string>();
    const [currentUploadJobId, setCurrentUploadJobId] = useState<string | undefined>(undefined);
    const [showUploadJobLog, setShowUploadJobLog] = useState(false);

    const [getUploadJob, {data: uploadJobData}] = useGetJobLazyQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });
    const [raiseOnceDateFilter, setRaiseOnceDateFilter] = useState<Date>();
    const [raiseRecurrDateFilter, setRaiseRecurrDateFilter] = useState<{raiseMonth: number, raiseDay: number}>();

    const [bonusOnceDateFilter, setBonusOnceDateFilter] = useState<Date>();
    const [bonusRecurrDateFilter, setBonusRecurrDateFilter] = useState<{bonusMonth: number, bonusDay: number}>();

    const [overtimeDateFilter, setOvertimeDateFilter] = useState<Date>();

    const [taxFilter, setTaxFilter] = useState<TaxFilter>(DEFAULT_TAX_FILTER);
    const [taxFilterOptions, setTaxFilterOptions] = useState<TaxFilterOptions>(DEFAULT_TAX_FILTER_OPTIONS);

    const tableRef = useRef(null);
    const rowsRef = useRef<FixedSizeList>(null);

    useEffect(
        () => {
            props.setSectionReady(AnalystSections.Payroll, true);
            payrollEmployeeDataRefetch();
        }, []
    );

    useEffect(
        () => {
            if (!payrollEmployeeDataLoading && payrollEmployeeData) {
                const selectedPositions = getPositions(payrollEmployeeData);
                const filtered = filterPayrollEmployees({
                    dbData: payrollEmployeeData,
                    propertyId: props.showSingleProperty,
                    nameSearchString: undefined,
                    filterPropertyIds: props.filterPropertyIds,
                    filterPositions: selectedPositions,
                    raiseOnceDateFilter: raiseOnceDateFilter,
                    raiseRecurrDateFilter: raiseRecurrDateFilter,
                    bonusOnceDateFilter: bonusOnceDateFilter,
                    bonusRecurrDateFilter: bonusRecurrDateFilter,
                    overtimeDateFilter: overtimeDateFilter,
                    taxFilter: taxFilter
                });
                const taxFilterOptions = buildTaxFilterOptions(payrollEmployeeData);
                const rows = buildPayrollDataRows(filtered, props.lockedProperties);
                setSelectedPositions(selectedPositions);
                setTaxFilterOptions(taxFilterOptions);
                setPayrollEmployeeDataRows(rows);
                deselectAll();
            }
        }, [payrollEmployeeDataLoading, payrollEmployeeData, props.lockedProperties]
    );

    useEffect(
        () => {
            if (payrollEmployeeData) {
                const filtered = filterPayrollEmployees({
                    dbData: payrollEmployeeData,
                    propertyId: props.showSingleProperty,
                    nameSearchString: employeeNameSearch,
                    filterPropertyIds: props.filterPropertyIds,
                    filterPositions: selectedPositions,
                    raiseOnceDateFilter: raiseOnceDateFilter,
                    raiseRecurrDateFilter: raiseRecurrDateFilter,
                    bonusOnceDateFilter: bonusOnceDateFilter,
                    bonusRecurrDateFilter: bonusRecurrDateFilter,
                    overtimeDateFilter: overtimeDateFilter,
                    taxFilter: taxFilter
                });
                const rows = buildPayrollDataRows(filtered, props.lockedProperties);
                setPayrollEmployeeDataRows(rows);
                deselectAll();
            }
        }, [
            employeeNameSearch,
            props.filterPropertyIds,
            selectedPositions,
            raiseOnceDateFilter,
            raiseRecurrDateFilter,
            bonusOnceDateFilter,
            bonusRecurrDateFilter,
            overtimeDateFilter,
            taxFilter,
            props.lockedProperties,
        ]
    );

    const bonusDates = useMemo(
        (): {once: Date[], recurring: {month: number, day: number}[]} => {
            const once: Date[] = [];
            const recurring: {month: number, day: number}[] = [];
            if (payrollEmployeeData) {
                for (const emp of payrollEmployeeData) {
                    for (const b of emp.bonuses) {
                        if (b.frequency == "ANNUALLY" && b.bonusMonth != undefined && b.bonusDay != undefined) {
                            recurring.push({month: b.bonusMonth, day: b.bonusDay})
                        }
                        else if (b.frequency == "ONCE" && b.onetimeBonusDate != undefined) {
                            once.push(ensureDateInLocale(b.onetimeBonusDate) ?? new Date());
                        }
                    }
                }
            }
            return {
                once: once.dedupe(d => d.toString()),
                recurring: recurring.dedupe(r => `${r.month}-${r.day}`)
            };
        },
        [payrollEmployeeData]
    );

    const raiseDates = useMemo(
        (): {once: Date[], recurring: {month: number, day: number}[]} => {
            const once: Date[] = [];
            const recurring: {month: number, day: number}[] = [];
            if (payrollEmployeeData) {
                for (const emp of payrollEmployeeData) {
                    for (const r of emp.raises) {
                        if (r.frequency == "ANNUALLY" && r.raiseMonth != undefined && r.raiseDay != undefined) {
                            recurring.push({month: r.raiseMonth, day: r.raiseDay});
                        }
                        else if (r.frequency == "ONCE" && r.onetimeRaiseDate != undefined) {
                            once.push(ensureDateInLocale(r.onetimeRaiseDate) ?? new Date());
                        }
                    }
                }
            }
            return {
                once: once.dedupe(d => d.toString()),
                recurring: recurring.dedupe(r => `${r.month}-${r.day}`)
            };
        },
        [payrollEmployeeData]
    );

    const overtimeDates = useMemo(
        (): {once: Date[], recurring: {month: number, day: number}[]} => {
            const once: Date[] = [];
            if (payrollEmployeeData) {
                for (const emp of payrollEmployeeData) {
                    for (const o of emp.overtime) {
                        once.push(ensureDateInLocale(o.effectiveStartDate) ?? new Date());
                    }
                }
            }
            return {
                once: once.dedupe(d => d.toString()),
                recurring: []
            };
        },
        [payrollEmployeeData]
    );

    const bonusesDisplayed = useMemo(() => Boolean(payrollEmployeeDataRows?.find(row => selectedPayrollEmployees.has(row.employeeId) && row.bonus != undefined)),
        [payrollEmployeeDataRows, selectedPayrollEmployees]
    );

    const raisesDisplayed = useMemo(() => Boolean(payrollEmployeeDataRows?.find(row => selectedPayrollEmployees.has(row.employeeId) && row.raise != undefined)),
        [payrollEmployeeDataRows, selectedPayrollEmployees]
    );

    const overtimesDisplayed = useMemo(() => Boolean(payrollEmployeeDataRows?.find(row => selectedPayrollEmployees.has(row.employeeId) && row.overtime != undefined)),
        [payrollEmployeeDataRows, selectedPayrollEmployees]
    );

    /**
     * useEffect(): editPayrollEmplloyeeId
     * Called as the first phase of editing an existing employee. Once the employee record is known, the drawer is opened.
     */
    useEffect(
        ()=>{
            if(!editPayrollEmplloyeeId){
                return;
            }
            setShowAnalystPayrollDrawer(true);
        },
        [editPayrollEmplloyeeId]
    );



    function selectAll() {
        if (payrollEmployeeDataRows) {
            setSelectedPayrollEmployees(new Set(payrollEmployeeDataRows.filter(e => !e.propertyLocked).map(e => e.employeeId).dedupe()));
        }
        else {
            setSelectedPayrollEmployees(new Set([]));
        }
    }

    function deselectAll() {
        setSelectedPayrollEmployees(new Set([]));
    }

    function selectEmployee(empData: PayrollEmployeeDataRow): void {
        if (empData.propertyLocked) {
            return;
        }
        setSelectedPayrollEmployees(prev => {
            const newSet = new Set(prev);
            newSet.add(empData.employeeId);

            return newSet;
        });
    }

    function deselectEmployee(id: string): void {
        setSelectedPayrollEmployees(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    }

    function handleDeleteEmployees(selectedIds: Set<string>): void {
        if(selectedIds.size === 0) {
            setShowBulkDeleteEmployees(false);
            toast.success("No Employees to Delete.");
        }
        deleteEmployees({
            variables: {
                employeeIds: Array.from(selectedIds)
            }
        }).then(() => {
            setShowBulkDeleteEmployees(false);
            toast.success("Employees Deleted.");
            payrollEmployeeDataRefetch();
        });
    }

    function handlebulkUpdateCompItems(
        what: PayrollCompensationItemType,
        effectiveStartDate: Date | undefined,
        percentOfSalary: number | undefined,
        contributionCap: number | null | undefined
    ) {
        if (currentProperty?.isAcquisitionProperty && effectiveStartDate) {
            effectiveStartDate = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(effectiveStartDate.toISOString(), currentProperty));
        }

        bulkUpdateCompItems({
            variables: {
                employeeIds: Array.from(selectedPayrollEmployees),
                type: what,
                createCalcType: [PayrollCompensationItemType.Medical, PayrollCompensationItemType.Vision, PayrollCompensationItemType.Dental].includes(what) ?
                        PayrollCompensationItemCalculationType.FixedAmount
                        :
                        PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation,
                input: {
                    annualCostCap: contributionCap === undefined ? undefined : contributionCap === null ? null : contributionCap.toString(),
                    costPerPayPeriod: percentOfSalary !== undefined ? percentOfSalary.toString() : undefined,
                    effectiveStartDate: effectiveStartDate !== undefined ? effectiveStartDate : undefined
                }
            }
        })
        .then(
            () => {
                setBulkUpdateCompItemType(undefined);
                toast.success(`Update Complete`);
                payrollEmployeeDataRefetch();
            }
        );
    }

    function handleBulkCreateBonuses(type: BulkUpdateBonusRaiseType, d: Date | undefined, val: number, month: number | undefined, day: number | undefined) {
        if (selectedPayrollEmployees.size > 0) {
            if (currentProperty?.isAcquisitionProperty && d) {
                d = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(d.toISOString(), currentProperty));
            }

            bulkCreateBonuses({
                variables: {
                    employeeIds: Array.from(selectedPayrollEmployees),
                    bonusType: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.recurring_fixed ? PayrollBonusType.FixedAmount : PayrollBonusType.PercentOfSalary,
                    frequency: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.once_percent ? PayrollBonusFrequency.Once : PayrollBonusFrequency.Annually,
                    amount: val.toString(),
                    bonusDay: day,
                    bonusMonth: month === undefined ? undefined : month - 1,
                    onetimeBonusDate: d,
                    effectiveStartDate: dateUtcNow().toISOString()
                }
            })
            .then(
                () => {
                    setShowBulkCreateBonuses(false);
                    toast.success(`Update Complete`);
                    setBonusOnceDateFilter(undefined);
                    setBonusRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkCreateRaises(type: BulkUpdateBonusRaiseType, d: Date | undefined, val: number, month: number | undefined, day: number | undefined) {
        if (selectedPayrollEmployees.size > 0) {
            if (currentProperty?.isAcquisitionProperty && d) {
                d = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(d.toISOString(), currentProperty));
            }

            bulkCreateRaises({
                variables: {
                    employeeIds: Array.from(selectedPayrollEmployees),
                    raiseType: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.recurring_fixed ? PayrollRaiseType.FixedAmount : PayrollRaiseType.PercentOfSalary,
                    frequency: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.once_percent ? PayrollRaiseFrequency.Once : PayrollRaiseFrequency.Annually,
                    amount: val.toString(),
                    raiseDay: day,
                    raiseMonth: month === undefined ? undefined : month - 1,
                    onetimeRaiseDate: d,
                    effectiveStartDate: dateUtcNow().toISOString()
                }
            })
            .then(
                () => {
                    setShowBulkCreateRaises(false);
                    toast.success(`Update Complete`);
                    setRaiseOnceDateFilter(undefined);
                    setRaiseRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkCreateOvertimes(startDate: Date | undefined, endDate: Date | undefined, hourlyRate: number, hoursWeek: number) {
        if (selectedPayrollEmployees.size > 0) {
            if (currentProperty?.isAcquisitionProperty && startDate) {
                startDate = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(startDate.toISOString(), currentProperty));

                if (endDate) {
                    endDate = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(endDate.toISOString(), currentProperty));
                }
            }

            if (!startDate) {
                return;
            }

            bulkCreateOvertimes({
                variables: {
                    employeeIds: Array.from(selectedPayrollEmployees),
                    hourlyRate: hourlyRate.toString(),
                    hoursWeek: hoursWeek.toString(),
                    effectiveStartDate: startDate,
                    effectiveEndDate: endDate
                }
            })
            .then(
                () => {
                    setShowBulkCreateOvertimes(false);
                    toast.success(`Update Complete`);
                    setOvertimeDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkUpdateBonuses(type: BulkUpdateBonusRaiseType, d: Date | undefined, val: number, month: number | undefined, day: number | undefined) {
        if (payrollEmployeeDataRows) {
            const bonusIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.bonus != undefined)
                .map(row => row.bonus?.id)
                .filterNull()
                .dedupe();

            if (currentProperty?.isAcquisitionProperty && d) {
                d = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(d.toISOString(), currentProperty));
            }

            bulkUpdateBonuses({
                variables: {
                    bonusIds: bonusIds,
                    bonusType: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.recurring_fixed ? PayrollBonusType.FixedAmount : PayrollBonusType.PercentOfSalary,
                    frequency: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.once_percent ? PayrollBonusFrequency.Once : PayrollBonusFrequency.Annually,
                    amount: val.toString(),
                    bonusDay: day,
                    bonusMonth: month === undefined ? undefined : month - 1,
                    onetimeBonusDate: d,
                    effectiveStartDate: dateUtcNow().toISOString()
                }
            })
            .then(
                () => {
                    setShowBulkUpdateBonuses(false);
                    toast.success(`Update Complete`);
                    setBonusOnceDateFilter(undefined);
                    setBonusRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkUpdateRaises(type: BulkUpdateBonusRaiseType, d: Date | undefined, val: number, month: number | undefined, day: number | undefined) {
        if (payrollEmployeeDataRows) {
            const raiseIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.raise != undefined)
                .map(row => row.raise?.id)
                .filterNull()
                .dedupe();

            if (currentProperty?.isAcquisitionProperty && d) {
                d = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(d.toISOString(), currentProperty));
            }

            bulkUpdateRaises({
                variables: {
                    raiseIds: raiseIds,
                    raiseType: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.recurring_fixed ? PayrollRaiseType.FixedAmount : PayrollRaiseType.PercentOfSalary,
                    frequency: type == BulkUpdateBonusRaiseType.once_fixed || type == BulkUpdateBonusRaiseType.once_percent ? PayrollRaiseFrequency.Once : PayrollRaiseFrequency.Annually,
                    amount: val.toString(),
                    raiseDay: day,
                    raiseMonth: month === undefined ? undefined : month - 1,
                    onetimeRaiseDate: d,
                    effectiveStartDate: dateUtcNow().toISOString()
                }
            })
            .then(
                () => {
                    setShowBulkUpdateRaises(false);
                    toast.success(`Update Complete`);
                    setRaiseOnceDateFilter(undefined);
                    setRaiseRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkUpdateOvertimes(startDate: Date | undefined, endDate: Date | undefined, hourlyRate: number, hoursWeek: number) {
        if (payrollEmployeeDataRows) {
            const overtimeIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.overtime != undefined)
                .map(row => row.overtime?.id)
                .filterNull()
                .dedupe();

            if (currentProperty?.isAcquisitionProperty && startDate) {
                startDate = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(startDate.toISOString(), currentProperty));

                if (endDate) {
                    endDate = ensureDateInLocale(transposeAcquisitionDateDisplayToDatabase(endDate.toISOString(), currentProperty));
                }
            }

            bulkUpdateOvertimes({
                variables: {
                    overtimeIds: overtimeIds,
                    hourlyRate: hourlyRate.toString(),
                    hoursWeek: hoursWeek.toString(),
                    effectiveStartDate: startDate,
                    effectiveEndDate: endDate
                }
            })
            .then(
                () => {
                    setShowBulkUpdateOvertimes(false);
                    toast.success(`Update Complete`);
                    setOvertimeDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkDeleteBonuses() {
        if (payrollEmployeeDataRows) {
            const bonusIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.bonus != undefined)
                .map(row => row.bonus?.id)
                .filterNull()
                .dedupe();

            bulkDeleteBonuses({
                variables: {
                    bonusIds: bonusIds
                }
            })
            .then(
                () => {
                    setShowBulkDeleteBonuses(false);
                    toast.success(`Delete Complete`);
                    setBonusOnceDateFilter(undefined);
                    setBonusRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkDeleteRaises() {
        if (payrollEmployeeDataRows) {
            const raiseIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.raise != undefined)
                .map(row => row.raise?.id)
                .filterNull()
                .dedupe();

            bulkDeleteRaises({
                variables: {
                    raiseIds: raiseIds
                }
            })
            .then(
                () => {
                    setShowBulkDeleteRaises(false);
                    toast.success(`Delete Complete`);
                    setRaiseOnceDateFilter(undefined);
                    setRaiseRecurrDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkDeleteOvertimes() {
        if (payrollEmployeeDataRows) {
            const overtimeIds = payrollEmployeeDataRows
                .filter(row => selectedPayrollEmployees.has(row.employeeId) && row.overtime != undefined)
                .map(row => row.overtime?.id)
                .filterNull()
                .dedupe();

            bulkDeleteOvertimes({
                variables: {
                    overtimeIds: overtimeIds
                }
            })
            .then(
                () => {
                    setShowBulkDeleteOvertimes(false);
                    toast.success(`Delete Complete`);
                    setOvertimeDateFilter(undefined);
                    payrollEmployeeDataRefetch();
                }
            );
        }
    }

    function handleBulkDeleteCompItems(itemType: PayrollCompensationItemType | null, employeeIds: Set<string>): void {
        if(employeeIds.size === 0 || itemType === null) {
            setShowBulkDeleteCompItems(false);
            toast.success(`Nothing to Delete`);
            return;
        }

        bulkDeleteCompItems({
            variables: {
                itemType: itemType,
                employeeIds: Array.from(employeeIds)
            }
        }).then(() => {
            setShowBulkDeleteCompItems(false);
            toast.success(`Delete Complete`);
            payrollEmployeeDataRefetch();
        });
    }

    // ensures the date displayed in the tables is correct for acquisition properties
    function dateDisplayString(date: string, currentProperty: PropertyContext): string {
        if (!date || date == "") {
            return "";
        }

        if (currentProperty.isAcquisitionProperty) {
            return acquisitionPropertyDateDisplayString(date, currentProperty);
        }

        return DateFormatter.format(ensureDateInLocale(date));
    }

    return (
        <div className={css.employeePageWrapper}>
            <ThemeProvider theme={ViziblyTheme}>
                <div className={css.employeePageInner}>
                    <div className={css.tableHeaderActions}>
                        <h5 className={css.tableHeaderActionsLeft}>
                            Employees
                            {
                                payrollEmployeeDataLoading && <Inline size={24} color={COLORS.PRIMARY_500} />
                            }
                        </h5>
                        <div className={css.tableHeaderActionsRight}>
                            <Button
                                    isBasic
                                    isDanger
                                    className={css.tableHeaderActionsButton}
                                    disabled={selectedPayrollEmployees.size === 0 || deleteEmployeesLoading}
                                    onClick={() => setShowBulkDeleteEmployees(true)}
                            >
                                Delete
                            </Button>
                            <Button
                                isPrimary
                                className={css.tableHeaderActionsButton}
                                onClick={() => {
                                    setEditPayrollEmployeeId("");
                                    setShowAnalystPayrollDrawer(true);
                                }}
                            >
                                Add Staff
                            </Button>
                            {userIsAdmin(user) && (
                                <>
                                    <PayrollEmployeeUploadButton
                                        budgetYear={props.budgetYear}
                                        jobCreated={(jobId) => setCurrentUploadJobId(jobId)}
                                        buttonClassName={css.tableHeaderActionsButton}
                                    />
                                    <Button
                                        className={css.tableHeaderActionsButton}
                                        onClick={fetchTemplate}
                                    >
                                        Upload Template
                                    </Button>
                                    <Button className={css.tableHeaderActionsButton} onClick={() => setShowUploadJobLog(true)}>
                                        <Button.StartIcon><History /></Button.StartIcon>
                                        View Job History
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    {userIsAdmin(user) && currentUploadJobId &&
                        <div style={{textAlign: "right", marginBottom: "0.5rem"}}>
                            <RunningJobRenderer jobId={currentUploadJobId} jobDone={job => {
                                if(job.status === JobStatus.Completed) {
                                    payrollEmployeeDataRefetch();
                                }
                            }} />
                        </div>
                    }

                    <div className={css.employeeTableWrapper} role="grid" aria-rowcount={payrollEmployeeData?.length ?? 0}>
                        <ScrollableTable className={css.employeeTable} size={"large"} ref={tableRef}
                            onScroll={
                                () => {
                                    if (tableRef.current && rowsRef.current) {
                                        const {scrollTop} = (tableRef.current as any);
                                        if (rowsRef.current != null) {
                                            rowsRef.current.scrollTo(scrollTop);
                                        }
                                    }
                                }
                            }
                        >
                            <ScrollableHead isSticky>
                                <ScrollableHeaderRow className={css.headerRowAbove}>
                                    <ScrollableHeaderCell className={`${css.checkboxCell}`} />
                                    <ScrollableHeaderCell className={`${css.numberCell}`} />
                                    <ScrollableHeaderCell className={`${css.nameCell}`}/>
                                    <ScrollableHeaderCell className={`${css.employeeIdCell}`} />
                                    <ScrollableHeaderCell className={`${css.statusCell}`} />
                                    <ScrollableHeaderCell className={`${css.positionTypeCell}`}/>
                                    <ScrollableHeaderCell className={`${css.workClassificationCell}`} />
                                    <ScrollableHeaderCell className={`${css.propertyCell}`} />
                                    <ScrollableHeaderCell className={`${css.dateCell}`} />
                                    <ScrollableHeaderCell className={`${css.dateCell}`} />
                                    <ScrollableHeaderCell colSpan={6} className={css.baseCompHeader}>BASE COMPENSATION</ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={5} className={css.raisesHeader}>
                                        RAISES
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkCreateRaises(true)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PlusIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkUpdateRaises(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !raisesDisplayed}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            isDanger
                                            size="small"
                                            onClick={() => setShowBulkDeleteRaises(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !raisesDisplayed}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={5} className={css.bonusesHeader}>
                                        BONUSES
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkCreateBonuses(true)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PlusIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkUpdateBonuses(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !bonusesDisplayed}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            isDanger
                                            size="small"
                                            onClick={() => setShowBulkDeleteBonuses(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !bonusesDisplayed}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={5} className={css.overtimeHeader}>
                                        OVERTIME
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkCreateOvertimes(true)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PlusIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setShowBulkUpdateOvertimes(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !overtimesDisplayed}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                            isBasic
                                            isDanger
                                            size="small"
                                            onClick={() => setShowBulkDeleteOvertimes(true)}
                                            disabled={selectedPayrollEmployees.size == 0 || !overtimesDisplayed}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={3} className={css.benefitHeader}>
                                        MEDICAL
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.Medical)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.Medical); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={3} className={css.benefitHeader}>
                                        DENTAL
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.Dental)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.Dental); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={3} className={css.benefitHeader}>
                                        VISION
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.Vision)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.Vision); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        401K
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.Retirement_401KMatch)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.Retirement_401KMatch); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        MEDICARE
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.MedicareTax)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.MedicareTax); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        SOCIAL SECURITY TAX
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.SocialSecurityTax)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.SocialSecurityTax); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                        </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        FEDERAL UNEMPLOYMENT TAX
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.FederalUnemploymentTax)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.FederalUnemploymentTax); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        STATE UNEMPLOYMENT TAX
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.StateUnemploymentTax)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.StateUnemploymentTax); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell colSpan={4} className={css.taxAndInsuranceHeader}>
                                        WORKERS COMPENSATION
                                        <Button
                                            isBasic
                                            size="small"
                                            onClick={() => setBulkUpdateCompItemType(PayrollCompensationItemType.WorkersCompensation)}
                                            disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <PencilIcon/>
                                        </Button>
                                        <Button
                                                isBasic
                                                isDanger
                                                size="small"
                                                onClick={() => {setBulkDeleteCompItemType(PayrollCompensationItemType.WorkersCompensation); setShowBulkDeleteCompItems(true);}}
                                                disabled={selectedPayrollEmployees.size == 0}
                                        >
                                            <XIcon/>
                                        </Button>
                                    </ScrollableHeaderCell>
                                </ScrollableHeaderRow>

                                <ScrollableHeaderRow className={css.headerRow}>
                                    <ScrollableHeaderCell className={css.checkboxCell}>
                                        <Field>
                                            <Checkbox
                                                className={css.employeeTableCheckbox}
                                                checked={payrollEmployeeDataRows && selectedPayrollEmployees.size == countUnlockedPayrollEmployees(payrollEmployeeDataRows)}
                                                onChange={() => {
                                                    payrollEmployeeDataRows && selectedPayrollEmployees.size == countUnlockedPayrollEmployees(payrollEmployeeDataRows) ? deselectAll() : selectAll();
                                                }}
                                                indeterminate={selectedPayrollEmployees.size != 0 && payrollEmployeeDataRows && selectedPayrollEmployees.size != countUnlockedPayrollEmployees(payrollEmployeeDataRows)}
                                            >
                                                <Label className={css.employeeTableCheckbox} />
                                            </Checkbox>
                                        </Field>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={css.numberCell}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.nameCell}`}>
                                        Staff Name
                                        <Field>
                                            <MediaInput
                                                className={css.tableHeaderActionsSearch}
                                                style={{height: "30px"}}
                                                placeholder="Search"
                                                isCompact
                                                onChange={evt =>
                                                    setEmployeeNameSearch(evt.target.value.toLowerCase())
                                                }
                                                start={
                                                    <SearchRounded style={{fontSize:"1.5rem"}} />
                                                }
                                            />
                                        </Field>
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.employeeIdCell}`}>Employee ID</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.statusCell}`}>Status</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.positionTypeCell}`}>
                                        Position Type
                                        <CustomZDDropdown
                                            initialSelectedItems={selectedPositions}
                                            applySelectedItems={function (items: any): void {
                                                setSelectedPositions(items);
                                            }}
                                            openDropdownPlaceholder={"Select Position Type(s)"}
                                            closedDropdownPlaceholder={undefined}
                                            options={payrollEmployeeData ? getPositions(payrollEmployeeData).map(v => ({value: v, label: v})).sort((a, b) => a.label.localeCompare(b.label)) : []}
                                            isError={undefined}
                                            isMulti
                                            allOption
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.workClassificationCell}`}>Work Classification</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.propertyCell}`}>Property</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCellMain}`}>Start Date</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCellMain}`}>End Date</ScrollableHeaderCell>
                                    {/* BASE COMPENSATION */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Type</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Annualized Amount</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Hourly Rate</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Hours/Week</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Pay Schedule</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>First Pay Date</ScrollableHeaderCell>
                                    {/* RAISES */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Type</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Percent (%)</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Amount ($)</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>
                                        Effective Date
                                        <PayrollDateFilter
                                            property={currentProperty}
                                            once={raiseDates.once}
                                            recurring={raiseDates.recurring}
                                            onSelectOnce={d => {setRaiseOnceDateFilter(d); setRaiseRecurrDateFilter(undefined)}}
                                            onSelectRecurring={(month, day) => {setRaiseOnceDateFilter(undefined); setRaiseRecurrDateFilter({raiseMonth: month, raiseDay: day})}}
                                            onSelectAny={() => {setRaiseOnceDateFilter(undefined); setRaiseRecurrDateFilter(undefined)}}
                                        />
                                    </ScrollableHeaderCell>
                                    {/* BONUSES */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Type</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Percent (%)</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Amount ($)</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>
                                        Bonus Date
                                        <PayrollDateFilter
                                            property={currentProperty}
                                            once={bonusDates.once}
                                            recurring={bonusDates.recurring}
                                            onSelectOnce={d => {setBonusOnceDateFilter(d); setBonusRecurrDateFilter(undefined)}}
                                            onSelectRecurring={(month, day) => {setBonusOnceDateFilter(undefined); setBonusRecurrDateFilter({bonusMonth: month, bonusDay: day})}}
                                            onSelectAny={() => {setBonusOnceDateFilter(undefined); setBonusRecurrDateFilter(undefined)}}
                                        />
                                    </ScrollableHeaderCell>
                                    {/* OVERTIME */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Hourly Rate</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Hours/Week</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>
                                        Start Date
                                        <PayrollDateFilter
                                            property={currentProperty}
                                            once={overtimeDates.once}
                                            recurring={overtimeDates.recurring}
                                            onSelectOnce={d => setOvertimeDateFilter(d)}
                                            onSelectRecurring={() => { /* no-op */}}
                                            onSelectAny={() => setOvertimeDateFilter(undefined)}
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>End Date</ScrollableHeaderCell>
                                    {/* MEDICAL */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>{payrollCompItemConfigPayPeriodText(payrollCompItemConfig.medical)}</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* DENTAL */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>{payrollCompItemConfigPayPeriodText(payrollCompItemConfig.dental)}</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* VISION */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>{payrollCompItemConfigPayPeriodText(payrollCompItemConfig.vision)}</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* 401K */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="r401k"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* MEDICARE */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="medicare"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* SOCIAL SECURITY TAX */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="socialSecurity"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* FEDERAL UNEMPLOYMENT TAX */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="futa"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* STATE UNEMPLOYMENT TAX */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="suta"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                    {/* WORKERS COMPENSATION */}
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>#</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>
                                        % of Salary
                                        <PayrollTaxPercentFilter
                                            setTaxFilter={setTaxFilter}
                                            taxFilter={taxFilter}
                                            taxFilterOptions={taxFilterOptions}
                                            what="wc"
                                        />
                                    </ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.valueCell}`}>Contribution Cap</ScrollableHeaderCell>
                                    <ScrollableHeaderCell className={`${css.dateCell}`}>Effective Date</ScrollableHeaderCell>
                                </ScrollableHeaderRow>
                            </ScrollableHead>
                            {payrollEmployeeDataRows != undefined && tableRef.current != null &&
                                <FixedSizeList
                                    itemCount={payrollEmployeeDataRows?.length ?? 0}
                                    ref={rowsRef}
                                    itemSize={40}
                                    height={800}
                                    width="100%"
                                    outerElementType={SuppressExtraDiv}
                                    innerElementType={ScrollableBody}
                                >
                                    {
                                        ({ index, style }) => {
                                            const empRow = payrollEmployeeDataRows[index];
                                            const styleUpdated = {
                                                ...style,
                                            };
                                            if (style.top != undefined) {
                                                if (typeof style.top == "string") {
                                                    styleUpdated.top = `calc(108 + ${style.top})`;
                                                }
                                                else if (typeof style.top == "number") {
                                                    styleUpdated.top = style.top + 108;
                                                }
                                            }
                                            if (!empRow || !currentProperty) {
                                                return null;
                                            }
                                            return (
                                                <ScrollableRow key={empRow.employeeId + empRow.empRowNum.toString()} style={styleUpdated} className={css.bodyRow}>
                                                    <ScrollableCell className={css.checkboxCell}>
                                                        {empRow.employee != undefined ?
                                                            <>
                                                                {empRow.propertyLocked ?
                                                                    <Tooltip
                                                                        type="light"
                                                                        placement="end"
                                                                        content={"Employee belongs to a locked property."}
                                                                    >
                                                                        <Lock className={css.lockIcon} />
                                                                    </Tooltip>
                                                                    :
                                                                    <Field>
                                                                        <Checkbox
                                                                            className={css.employeeTableCheckbox}
                                                                            checked={selectedPayrollEmployees.has(empRow.employeeId)}
                                                                            disabled={empRow.propertyLocked}
                                                                            onChange={() => {
                                                                                selectedPayrollEmployees.has(empRow.employeeId) ? deselectEmployee(empRow.employeeId) : selectEmployee(empRow);
                                                                            }}
                                                                        >
                                                                            <Label className={css.employeeTableCheckbox} />
                                                                        </Checkbox>
                                                                    </Field>
                                                                }
                                                            </>
                                                        :
                                                        null
                                                        }
                                                    </ScrollableCell>
                                                    <ScrollableCell className={`${css.numberCell}`}>{empRow.empNum != undefined ? empRow.empNum : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.nameCell}`}>
                                                        <div style={{display: "flex", flexDirection: "row"}}>
                                                        {empRow.employee != undefined ? empRow.employee.firstName + (empRow.employee.lastName ? " " + empRow.employee.lastName : "") : ""}
                                                        {empRow.employee != undefined ?
                                                        <Button
                                                            isBasic
                                                            size="small"
                                                            className={css.editButton}
                                                            onClick={() => {
                                                                setEditPayrollEmployeeId(empRow.employeeId);
                                                                setShowAnalystPayrollDrawer(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        :
                                                        null
                                                        }
                                                        </div>
                                                    </ScrollableCell>
                                                    <ScrollableCell className={`${css.employeeIdCell}`}>{empRow.employee?.externalSystemEmployeeId ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.statusCell}`}>{empRow.employee?.type ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.positionTypeCell}`}>{empRow.position?.name ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.workClassificationCell}`}>{empRow.employee?.classification ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.propertyCell}`}>{empRow.propertyAttribution?.property.name ?? ""}{empRow.propertyAttribution?.attributionPercentage ? ` (${empRow.propertyAttribution.attributionPercentage}%)` : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.employee?.startDate, currentProperty)}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.employee?.terminationDate, currentProperty)}</ScrollableCell>
                                                    {/* BASE COMPENSATION */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.baseComp?.compensationType ?? "-"}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.baseComp?.annualCompensation ? `$${empRow.baseComp?.annualCompensation}` : "-"}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.baseComp?.hourlyCompensation ? `$${empRow.baseComp?.hourlyCompensation}` : "-"}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.baseComp?.expectedHoursPerWeek}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{/* Pay Schedule (PayrollPayFrequency in schema.gql) */}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.employee?.startDate, currentProperty)}</ScrollableCell>
                                                    {/* RAISES */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.raise?.raiseType ?? "-"}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.raise?.raiseType == "PERCENT_OF_SALARY" ? (empRow.raise?.amount ?? "") : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.raise?.raiseType == "FIXED_AMOUNT" ? (empRow.raise?.amount ?? "") : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>
                                                        {empRow.raise?.frequency == "ONCE" && empRow.raise?.onetimeRaiseDate ?
                                                        dateDisplayString(empRow.raise?.onetimeRaiseDate, currentProperty)
                                                        :
                                                        empRow.raise?.frequency == "ANNUALLY" && empRow.raise?.raiseDay != undefined && empRow.raise?.raiseMonth != undefined ?
                                                        `0${empRow.raise?.raiseMonth + 1}`.slice(-2) + "/" + `0${empRow.raise?.raiseDay}`.slice(-2)
                                                        :
                                                        ""
                                                        }
                                                    </ScrollableCell>
                                                    {/* BONUSES */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.bonus?.bonusType ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.bonus?.bonusType == "PERCENT_OF_SALARY" ? (empRow.bonus?.amount ?? "") : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.bonus?.bonusType == "FIXED_AMOUNT" ? (empRow.bonus?.amount ?? "") : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>
                                                        {empRow.bonus?.frequency == "ONCE" && empRow.bonus?.onetimeBonusDate ?
                                                        dateDisplayString(empRow.bonus?.onetimeBonusDate, currentProperty)
                                                        :
                                                        empRow.bonus?.frequency == "ANNUALLY" && empRow.bonus?.bonusDay != undefined && empRow.bonus?.bonusMonth != undefined ?
                                                        `0${empRow.bonus?.bonusMonth + 1}`.slice(-2) + "/" + `0${empRow.bonus?.bonusDay}`.slice(-2)
                                                        :
                                                        ""
                                                        }
                                                    </ScrollableCell>
                                                    {/* OVERTIME */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.overtime?.hourlyCompensation ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.overtime?.expectedHoursPerWeek ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.overtime?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.overtime?.effectiveEndDate, currentProperty)}</ScrollableCell>
                                                    {/* MEDICAL */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.medical?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.medical?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* DENTAL */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.dental?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.dental?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* VISION */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.vision?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.vision?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* 401K */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.r401k?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.r401k?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.r401k?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* MEDICARE */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.medicare?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.medicare?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.medicare?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* SOCIAL SECURITY TAX */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.socialSecurity?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.socialSecurity?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.socialSecurity?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* FEDERAL UNEMPLOYMENT TAX */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.futa?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.futa?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.futa?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* STATE UNEMPLOYMENT TAX */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.suta?.costPerPayPeriod ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.suta?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.suta?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                    {/* WORKERS COMPENSATION */}
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.empRowNum}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.wc?.costPerPayPeriod ? Number(empRow.wc?.costPerPayPeriod) : ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.valueCell}`}>{empRow.wc?.annualCostCap ?? ""}</ScrollableCell>
                                                    <ScrollableCell className={`${css.dateCell}`}>{dateDisplayString(empRow.wc?.effectiveStartDate, currentProperty)}</ScrollableCell>
                                                </ScrollableRow>
                                            );
                                        }
                                }
                                </FixedSizeList>
                            }

                        </ScrollableTable>
                    </div>
                </div>
                <div>
                {
                    showAnalystPayrollDrawer && editPayrollEmplloyeeId !== undefined && props.budgetYear !== undefined && <AnalystPayrollDrawer
                        employeeID={editPayrollEmplloyeeId}
                        show={showAnalystPayrollDrawer}
                        budgetYear={props.budgetYear}
                        lockedProperties={props.lockedProperties}
                        handleClose={(wasAnyUpdatesPersistedOnBE) => {
                            setEditPayrollEmployeeId(undefined);
                            setShowAnalystPayrollDrawer(false);
                            if(wasAnyUpdatesPersistedOnBE) {
                                payrollEmployeeDataRefetch();
                            }
                        }}
                    />
                }
                {
                    bulkUpdateCompItemType &&
                    <BulkUpdateBenefitTaxIns
                        onClose={() => setBulkUpdateCompItemType(undefined)}
                        onConfirm={handlebulkUpdateCompItems}
                        loading={bulkUpdateCompItemsLoading}
                        updateWhat={bulkUpdateCompItemType}
                    />
                }
                {
                    showBulkCreateRaises &&
                    <BulkUpdateBonusRaise
                        isCreate
                        onClose={() => setShowBulkCreateRaises(false)}
                        onConfirmOnceFixed={(date, amount) => handleBulkCreateRaises(BulkUpdateBonusRaiseType.once_fixed, date, amount, undefined, undefined)}
                        onConfirmOncePercent={(date, percent) => handleBulkCreateRaises(BulkUpdateBonusRaiseType.once_percent, date, percent, undefined, undefined)}
                        onConfirmRecurringFixed={(month, day, amount) => handleBulkCreateRaises(BulkUpdateBonusRaiseType.recurring_fixed, undefined, amount, month, day)}
                        onConfirmRecurringPercent={(month, day, percent) => handleBulkCreateRaises(BulkUpdateBonusRaiseType.recurring_percent, undefined, percent, month, day)}
                        loading={bulkCreateRaisesLoading}
                        updateWhat="raises"
                    />
                }
                {
                    showBulkCreateBonuses &&
                    <BulkUpdateBonusRaise
                        isCreate
                        onClose={() => setShowBulkCreateBonuses(false)}
                        onConfirmOnceFixed={(date, amount) => handleBulkCreateBonuses(BulkUpdateBonusRaiseType.once_fixed, date, amount, undefined, undefined)}
                        onConfirmOncePercent={(date, percent) => handleBulkCreateBonuses(BulkUpdateBonusRaiseType.once_percent, date, percent, undefined, undefined)}
                        onConfirmRecurringFixed={(month, day, amount) => handleBulkCreateBonuses(BulkUpdateBonusRaiseType.recurring_fixed, undefined, amount, month, day)}
                        onConfirmRecurringPercent={(month, day, percent) => handleBulkCreateBonuses(BulkUpdateBonusRaiseType.recurring_percent, undefined, percent, month, day)}
                        loading={bulkCreateBonusesLoading}
                        updateWhat="bonuses"
                    />
                }
                {
                    showBulkCreateOvertimes &&
                    <BulkUpdateOvertime
                        isCreate
                        onClose={() => setShowBulkCreateOvertimes(false)}
                        onConfirm={(startDate, endDate, hourlyRate, hoursWeek) => handleBulkCreateOvertimes(startDate, endDate, hourlyRate, hoursWeek)}
                        loading={bulkCreateOvertimesLoading}
                    />
                }
                {
                    showBulkUpdateRaises &&
                    <BulkUpdateBonusRaise
                        isCreate
                        onClose={() => setShowBulkUpdateRaises(false)}
                        onConfirmOnceFixed={(date, amount) => handleBulkUpdateRaises(BulkUpdateBonusRaiseType.once_fixed, date, amount, undefined, undefined)}
                        onConfirmOncePercent={(date, percent) => handleBulkUpdateRaises(BulkUpdateBonusRaiseType.once_percent, date, percent, undefined, undefined)}
                        onConfirmRecurringFixed={(month, day, amount) => handleBulkUpdateRaises(BulkUpdateBonusRaiseType.recurring_fixed, undefined, amount, month, day)}
                        onConfirmRecurringPercent={(month, day, percent) => handleBulkUpdateRaises(BulkUpdateBonusRaiseType.recurring_percent, undefined, percent, month, day)}
                        loading={bulkUpdateRaisesLoading}
                        updateWhat="raises"
                    />
                }
                {
                    showBulkUpdateBonuses &&
                    <BulkUpdateBonusRaise
                        onClose={() => setShowBulkUpdateBonuses(false)}
                        onConfirmOnceFixed={(date, amount) => handleBulkUpdateBonuses(BulkUpdateBonusRaiseType.once_fixed, date, amount, undefined, undefined)}
                        onConfirmOncePercent={(date, percent) => handleBulkUpdateBonuses(BulkUpdateBonusRaiseType.once_percent, date, percent, undefined, undefined)}
                        onConfirmRecurringFixed={(month, day, amount) => handleBulkUpdateBonuses(BulkUpdateBonusRaiseType.recurring_fixed, undefined, amount, month, day)}
                        onConfirmRecurringPercent={(month, day, percent) => handleBulkUpdateBonuses(BulkUpdateBonusRaiseType.recurring_percent, undefined, percent, month, day)}
                        loading={bulkUpdateBonusesLoading}
                        updateWhat="bonuses"
                    />
                }
                {
                    showBulkUpdateOvertimes &&
                    <BulkUpdateOvertime
                        onClose={() => setShowBulkUpdateOvertimes(false)}
                        onConfirm={(startDate, endDate, hourlyRate, hoursWeek) => handleBulkUpdateOvertimes(startDate, endDate, hourlyRate, hoursWeek)}
                        loading={bulkUpdateOvertimesLoading}
                    />
                }
                {
                    showBulkDeleteEmployees &&
                    <BulkDeleteConfirm
                            what={selectedPayrollEmployees.size.toString() + " employees"}
                            onClose={() => setShowBulkDeleteEmployees(false)}
                            onConfirm={() => handleDeleteEmployees(selectedPayrollEmployees)}
                            loading={deleteEmployeesLoading} />
                }
                {
                    showBulkDeleteBonuses &&
                    <BulkDeleteConfirm
                        what="bonuses"
                        onClose={() => setShowBulkDeleteBonuses(false)}
                        onConfirm={() => handleBulkDeleteBonuses()}
                        loading={bulkDeleteBonusesLoading}
                    />
                }
                {
                    showBulkDeleteRaises &&
                    <BulkDeleteConfirm
                        what="raises"
                        onClose={() => setShowBulkDeleteRaises(false)}
                        onConfirm={() => handleBulkDeleteRaises()}
                        loading={bulkDeleteRaisesLoading}
                    />
                }
                {
                    showBulkDeleteOvertimes &&
                    <BulkDeleteConfirm
                        what="overtimes"
                        onClose={() => setShowBulkDeleteOvertimes(false)}
                        onConfirm={() => handleBulkDeleteOvertimes()}
                        loading={bulkDeleteOvertimesLoading}
                    />
                }
                {
                    showBulkDeleteCompItems && bulkDeleteCompItemType &&
                    <BulkDeleteConfirm
                            what={bulkDeleteCompItemType}
                            onClose={() => setShowBulkDeleteCompItems(false)}
                            onConfirm={() => handleBulkDeleteCompItems(bulkDeleteCompItemType, selectedPayrollEmployees)}
                            loading={bulkDeleteCompItemsLoading} />
                }
                {
                    showUploadJobLog &&
                    <Modal
                        className={css.jobLogModal}
                        onClose={() => setShowUploadJobLog(false)}
                    >
                        <Header>
                            Payroll Employee Upload Jobs
                        </Header>
                        <ModalBody className={css.jobLogModalBody}>
                            <ListJobs
                                jobType={JobType.PayrollEmployeesFileUpload}
                                visibleColumns={[
                                    ListJobsDisplayColumn.user,
                                    ListJobsDisplayColumn.status,
                                    ListJobsDisplayColumn.created,
                                    ListJobsDisplayColumn.started,
                                    ListJobsDisplayColumn.ended,
                                    ListJobsDisplayColumn.errorMessage,
                                    ListJobsDisplayColumn.details,
                                ]}
                            />
                        </ModalBody>
                        <Close aria-label="Close modal" />
                    </Modal>
                }
                </div>
            </ThemeProvider>
        </div>
    );
}